import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface HistoricDeclarationFilters {
  department?: string;
  page?: number;
  itemsForPage?: number;
  clients?: any[];
  models?: number[];
  presentations?: string[];
  only?: string;
  withComments?: boolean | null;
  year?: number;
  relatedDeclarations?: string;
  startPeriod?: string;
  endPeriod?: string;
  responsibles?: number[];
  executors?: number[];
  status?: string[];
  hasNotifications?: boolean | null;
  clientCodes?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class DeclarationsService {

  constructor(
    private http: HttpClient,
  ) { }

  private appendParam(path: string, key: string, value: string | number | boolean): string {
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}${key}=${encodeURIComponent(String(value))}`;
  }

  private buildHistoricQueryPath(filters: HistoricDeclarationFilters, includePagination = true): string {
    let path = '';

    if (filters.department && filters.department !== 'GENERAL') {
      path = this.appendParam(path, 'department', filters.department);
    }

    if (includePagination && filters.itemsForPage != null) {
      path = this.appendParam(path, 'page', filters.page ?? 0);
      path = this.appendParam(path, 'items', filters.itemsForPage);
    }

    path = this.appendParam(path, 'isHistoric', true);

    if (filters.clients?.length) {
      const clientsArray = filters.clients.map((client: any) => client.id ?? client).join(',');
      path = this.appendParam(path, 'clients', clientsArray);
    }

    if (filters.clientCodes?.length) {
      path = this.appendParam(path, 'clientCodes', filters.clientCodes.join(','));
    }

    if (filters.models?.length) {
      path = this.appendParam(path, 'models', filters.models.join(','));
    }

    if (filters.only && filters.only !== 'all') {
      path = this.appendParam(path, 'only', filters.only);
    }

    if (filters.withComments === true || filters.withComments === false) {
      path = this.appendParam(path, 'hasComments', filters.withComments);
    }

    if (filters.presentations?.length) {
      path = this.appendParam(path, 'presentations', filters.presentations.join(','));
    }

    if (filters.year) {
      path = this.appendParam(path, 'year', filters.year);
    }

    if (filters.relatedDeclarations) {
      path = this.appendParam(path, 'relatedDeclarations', filters.relatedDeclarations);
    }

    if (filters.startPeriod) {
      path = this.appendParam(path, 'startPeriod', filters.startPeriod);
    }

    if (filters.endPeriod) {
      path = this.appendParam(path, 'endPeriod', filters.endPeriod);
    }

    if (filters.responsibles?.length) {
      path = this.appendParam(path, 'responsibles', filters.responsibles.join(','));
    }

    if (filters.executors?.length) {
      path = this.appendParam(path, 'executors', filters.executors.join(','));
    }

    if (filters.status?.length) {
      path = this.appendParam(path, 'status', filters.status.join(','));
    }

    if (filters.hasNotifications === true || filters.hasNotifications === false) {
      path = this.appendParam(path, 'hasNotifications', filters.hasNotifications);
    }

    return path;
  }

  async getHistoricDeclarations(filters: HistoricDeclarationFilters) {
    const path = this.buildHistoricQueryPath(filters, true);
    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/declarations${path}`));
  }

  // eslint-disable-next-line max-params
  async getDeclarations(department?: any, currentPage?: number, itemsForPage?: number, clients?: any[], fase?: any, models?: any, presentations?: any, only?: any, isHistoric?: boolean, withComments?: boolean, years?: any, relatedDeclarations?: any, startPeriod?: any, endPeriod?: any, responsibles?: any[], executors?: any[], status?: string[], hasNotifications?: boolean | null, clientCodes?: number[]) {
    if (isHistoric) {
      return this.getHistoricDeclarations({
        department,
        page: currentPage,
        itemsForPage,
        clients,
        models,
        presentations,
        only,
        withComments,
        year: years,
        relatedDeclarations,
        startPeriod,
        endPeriod,
        responsibles,
        executors,
        status,
        hasNotifications,
        clientCodes,
      });
    }

    let path = "";
    if(department && department !== 'GENERAL'){
      path += `?department=${department}`;
      if(itemsForPage){
        path += `&page=${currentPage}`;
        path += `&items=${itemsForPage}`;
      }
    }else {
      if(itemsForPage){
        path += `?page=${currentPage}`;
        path += `&items=${itemsForPage}`;
      }
    }
    if (clients){
      if(clients.length > 0){
        const clientsArray = clients.map((client: any) => client.id).join(',');
        path += `${path.includes('?') ? '&' : '?'}clients=${clientsArray}`;
      }
    }
    if (fase){
      const fasesArray = fase.id;
      path += `${path.includes('?') ? '&' : '?'}phases=${fasesArray}`;
    }
    if(models){
      if (models.length > 0){
        const modelsArray = models.join(',');
        path += `${path.includes('?') ? '&' : '?'}models=${modelsArray}`;
      }
    }
    if (only && only !== 'all'){
      path += `${path.includes('?') ? '&' : '?'}only=${only}`;
    }

    if (withComments){
      path += `${path.includes('?') ? '&' : '?'}hasComments=${withComments}`;
    }
    if(presentations){
      if (presentations.length > 0){
        path += `${path.includes('?') ? '&' : '?'}presentations=${presentations}`;
      }
    }

    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/declarations` + path)); 
  }

  async getDownloadHistoricDeclarations(filters: HistoricDeclarationFilters) {
    const path = this.buildHistoricQueryPath(filters, false);
    const blob = await lastValueFrom(
      this.http.get(`${environment.apiUrl}/declarations/download-historic${path}`, {
        responseType: 'blob'
      })
    );

    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = 'declaraciones_historicas.xlsx';
    a.click();
    URL.revokeObjectURL(objectUrl);
  }

  async getDeclarationsById(id: number) {
    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/declarations/detail/${id}`));

  }

  async getObservationsByDeclaration(id: number, currentPage: number, itemsForPage: number) {
    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/observations/by-declaration/${id}?page=${currentPage}&items=${itemsForPage}`));
  }

  async getDeclarationsInMass(ids: number[]) {
    const idsString = ids.map(id => 'ids=' + id).join('&');
    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/declarations/detail-in-mass?${idsString}`));

  }

  async editDeclaration(id: number, body: any) {
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/declarations/edit/${id}`, body));
  }

  async taskStatusUpdate(task: any) {
    const body = {
        type: "STATUS_TASK",
        change: task.change
    };
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/status-tasks/edit/${task.id}`, body));
  }

  async disableTaskUpdate(task: any) {
    const body = {
      type: "DISABLE_TASK",
      change: task.isDisabled
    }
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/status-tasks/edit/${task.id}`, body));
  }

  async disableTaskUpdateInMass(task: any) {
    let changedTasks = []
    for(let i=0; i< task.ids.length; i++) {
      changedTasks.push(
        {
          id: task.ids[i],
          type: "DISABLE_TASK",
          change: task.isDisabled
        }
      );
    }
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/status-tasks/update-mass`, changedTasks));
  }



  async taskExecutorUpdate(taskId: any, executorId: any) {
    const body = {
        type: "EXECUTOR",
        change: executorId
    };
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/status-tasks/edit/${taskId}`, body));
  }

  async massTaskStatusUpdate(changedTasks: any[]) {
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/status-tasks/update-mass`, changedTasks));
  }

  async createObservation(observation: any) {

    return await lastValueFrom<any>(this.http.post(`${environment.apiUrl}/observations`, observation));
  }

  async createObservationInMass(observations: any) {
    return await lastValueFrom<any>(this.http.post(`${environment.apiUrl}/observations/create-mass`, observations));
  }

  async deleteObservation(id: number) {
    return await lastValueFrom<any>(this.http.delete(`${environment.apiUrl}/observations/${id}`));
  }

  async getActivityByDeclaration(currentPage: number, itemsForPage: number, id: number) {
    return await lastValueFrom<any>(this.http.get(`${environment.apiUrl}/activities/get-by-declaration/${id}?page=${currentPage}&items=${itemsForPage}`));
  }

  async createAsociedDeclaration(id: any, type: any) {
    const body = {
      declarationId: id,
      type: type
    }
    return await lastValueFrom<any>(this.http.post(`${environment.apiUrl}/declarations`, body));
  }

  async changeStatusDeclarationInMass(ids: any, status: any) { 
    let body = ids.map((id: any) => {
      return {
        id: id,
        type: "STATUS",
        change: status
      }
     });
    return await lastValueFrom<any>(this.http.put(`${environment.apiUrl}/declarations/update-mass`, body));
  }

  async createExtraTemporaryDeclaration(declaration) {
    return await lastValueFrom<any>(this.http.post(`${environment.apiUrl}/declarations/extraTemporary`, declaration));
  }


}
